import { useState } from "react"
import { format } from "date-fns"
import { observer } from "mobx-react-lite"

import type {
  BulkOperationsViewProps,
  PerformanceStatsViewProps,
  QueryDetailsData,
  QueryErrorData,
  QueryErrorsViewProps,
  QueryStatusOverviewViewProps,
} from "@/domains/diagnostics/types"
import type { Sort } from "@/domains/table-processor"
import { useScaleApplication } from "@/domains/application/use-scale-application"

import { OrchestratedQueriesView } from "../views/orchestrated-queries-view"

export const OrchestratedQueriesComposite = observer(() => {
  const scaleApp = useScaleApplication()
  const store = scaleApp.OrchestratedQueryStore
  const [sortBy, setSortBy] = useState<Sort | undefined>(undefined)

  // Format interval in milliseconds to readable format
  const formatInterval = (ms: number): string => {
    if (ms < 1000) {
      return `${ms}ms`
    }
    if (ms < 60000) {
      return `${(ms / 1000).toFixed(1)}s`
    }
    if (ms < 3600000) {
      return `${(ms / 60000).toFixed(1)}m`
    }
    return `${(ms / 3600000).toFixed(1)}h`
  }

  // Format timestamp to time string
  const formatTime = (timestamp: number): string => {
    if (timestamp === 0) {
      return "Never"
    }
    return format(new Date(timestamp), "HH:mm:ss")
  }

  // Format timestamp to full date string
  const formatFullDate = (timestamp: number): string => {
    if (timestamp === 0) {
      return "Never"
    }
    return format(new Date(timestamp), "MM/dd/yyyy, HH:mm:ss")
  }

  // Format duration in milliseconds
  const formatDuration = (ms: number): string => {
    return `${ms.toFixed(2)}ms`
  }

  // Query Status Overview Data
  const statusOverview: QueryStatusOverviewViewProps = {
    data: {
      totalQueries: store.totalQueries,
      activeQueries: store.totalActiveQueries,
      runningQueries: store.totalRunningQueries,
      errorQueries: store.totalErrorQueries,
    },
  }

  // Performance Stats Data
  const performanceStats: PerformanceStatsViewProps = {
    data: {
      totalExecutions: store.performanceStats.totalExecutions,
      totalExecutionTime: store.performanceStats.totalExecutionTime,
      averageExecutionTime: store.performanceStats.averageExecutionTime,
      queriesWithErrors: store.performanceStats.errorQueries,
    },
  }

  // Bulk Operations Handlers
  const bulkOperations: BulkOperationsViewProps = {
    onStartAll: () => {
      store.startAll()
    },
    onStopAll: () => {
      store.stopAll()
    },
    onForceExecuteAll: () => {
      store.forceExecuteAll()
    },
    onDisposeAll: () => {
      store.dispose()
    },
  }

  // Query Details Data
  let queryDetails: QueryDetailsData[] = store.queries.map((query) => ({
    name: query.getName,
    uniqueId: query.getUniqueId,
    uniqueIdShort: query.getUniqueId.substring(0, 8),
    status: query.getIsActive ? "active" : "inactive",
    interval: formatInterval(query.getIntervalMs),
    lastExecution: formatTime(query.getLastExecutionTime),
    executionCount: query.getExecutionCount,
    averageExecutionTime: formatDuration(query.averageExecutionTime),
    lastExecutionDuration:
      query.getLastExecutionDuration > 0 ? formatDuration(query.getLastExecutionDuration) : undefined,
    isRunning: query.getIsRunning,
    query,
    rpcMetrics: query.getLastRpcMetrics, // Include RPC metrics if available
  }))

  // Apply sorting if sortBy is set
  if (sortBy) {
    queryDetails = [...queryDetails].sort((a, b) => {
      let valueA: string | number
      let valueB: string | number

      switch (sortBy.key) {
        case "name":
          valueA = a.name
          valueB = b.name
          break
        case "uniqueId":
          valueA = a.uniqueId
          valueB = b.uniqueId
          break
        case "status":
          valueA = a.status
          valueB = b.status
          break
        default:
          return 0
      }

      if (sortBy.type === "string") {
        if (sortBy.direction === "asc") {
          return String(valueA).localeCompare(String(valueB))
        } else {
          return String(valueB).localeCompare(String(valueA))
        }
      } else {
        if (sortBy.direction === "asc") {
          return Number(valueA) - Number(valueB)
        } else {
          return Number(valueB) - Number(valueA)
        }
      }
    })
  }

  // Query Errors Data
  const errors: QueryErrorData[] = store.errorQueries
    .filter((query) => query.getLastError)
    .map((query) => ({
      queryName: query.getName,
      errorMessage: query.getLastError?.message ?? "Unknown error",
      lastOccurred: formatFullDate(query.getLastExecutionTime),
    }))

  // Individual Query Handlers
  const handleStopQuery = (query: (typeof store.queries)[number]) => {
    query.stop()
  }

  const handleForceExecuteQuery = (query: (typeof store.queries)[number]) => {
    query.forceExecute()
  }

  // RPC Metrics Toggle Handler
  const handleToggleRpcMetrics = (enabled: boolean) => {
    if (enabled) {
      store.enableRpcMetrics()
    } else {
      store.disableRpcMetrics()
    }
  }

  return (
    <OrchestratedQueriesView
      statusOverview={statusOverview}
      performanceStats={performanceStats}
      bulkOperations={bulkOperations}
      queryDetails={{
        queries: queryDetails,
        onStopQuery: handleStopQuery,
        onForceExecuteQuery: handleForceExecuteQuery,
        sortBy,
        setSortBy,
      }}
      errors={{ errors }}
      rpcMetricsToggle={{
        enabled: store.rpcMetricsEnabled,
        onToggle: handleToggleRpcMetrics,
      }}
    />
  )
})
